import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { DollarSign, Edit2, Save, X, Plus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const RAMO_LABELS = { auto:'Automóvil', moto:'Motocicleta', truck:'Transporte', fleet:'Flota', home:'Hogar', commercial:'Comercio', building:'Edificio', life:'Vida', accident:'Accidentes', health:'Salud', liability:'RC', other:'Otro' };
const RAMOS = Object.keys(RAMO_LABELS);

export default function CommissionsPage() {
  const { policies, companies, updateCompany, addCompany } = useApp();
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [newCo, setNewCo] = useState({ name: '', commissionRate: 12 });
  const [showAdd, setShowAdd] = useState(false);
  const [expandRamos, setExpandRamos] = useState(null); // id de compañía con ramos abiertos
  const [nuevoRamo, setNuevoRamo] = useState({ ramo: '', rate: '' });
  // Liquidaciones que mandan las compañías por mail y carga el bot de correo.
  // Este endpoint es solo-admin, igual que esta pantalla.
  const [liquidaciones, setLiquidaciones] = useState([]);
  React.useEffect(() => {
    const token = localStorage.getItem('nebb_token');
    if (!token) return;
    fetch('/api/comisiones', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (j) setLiquidaciones(j.comisiones || []); })
      .catch(() => {});
  }, []);

  const active = policies.filter(p => p.status === 'active' || p.status === 'endorsed');

  // Devuelve la tasa para un ramo: excepción si existe, si no la general
  const rateForRamo = (co, ramo) => {
    const ex = co.ramoRates && co.ramoRates[ramo];
    return (ex != null && ex !== '') ? Number(ex) : Number(co.commissionRate || 0);
  };

  const companyData = companies.map(co => {
    const coPolicies = active.filter(p => p.company === co.name);
    const totalPremium = coPolicies.reduce((s, p) => s + (p.premium || 0), 0);
    // Comisión: suma por póliza usando la tasa de su ramo
    const estimated = coPolicies.reduce((s, p) => {
      return s + (p.premium || 0) * (rateForRamo(co, p.type) / 100);
    }, 0);
    return { ...co, count: coPolicies.length, totalPremium, estimated };
  }).filter(c => c.count > 0 || true);

  const totalEstimated = companyData.reduce((s, c) => s + c.estimated, 0);
  const totalPremium = companyData.reduce((s, c) => s + c.totalPremium, 0);

  const saveRate = (id) => {
    updateCompany(id, { commissionRate: Number(editVal) });
    setEditing(null);
  };

  const agregarRamo = (co) => {
    if (!nuevoRamo.ramo || nuevoRamo.rate === '') return;
    const ramoRates = { ...(co.ramoRates || {}), [nuevoRamo.ramo]: Number(nuevoRamo.rate) };
    updateCompany(co.id, { ramoRates });
    setNuevoRamo({ ramo: '', rate: '' });
  };

  const quitarRamo = (co, ramo) => {
    const ramoRates = { ...(co.ramoRates || {}) };
    delete ramoRates[ramo];
    updateCompany(co.id, { ramoRates });
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    await addCompany(newCo);
    setNewCo({ name: '', commissionRate: 12 });
    setShowAdd(false);
  };

  const chartData = companyData.filter(c => c.count > 0).map(c => ({ name: c.name, comision: Math.round(c.estimated) }));

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ color: 'var(--amber)' }}>Comisiones</h1>
          <p className="page-subtitle">Solo visible para Administrador · Basado en primas de pólizas activas</p>
        </div>
        <button className="btn btn-amber btn-sm" onClick={() => setShowAdd(!showAdd)}>{showAdd ? 'Cancelar' : '+ Compañía'}</button>
      </div>

      {/* Add company */}
      {showAdd && (
        <div className="card" style={{ marginBottom: 20, borderColor: 'rgba(245,166,35,0.2)' }}>
          <form onSubmit={handleAddCompany} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: 2, minWidth: 160 }}>
              <label className="form-label">Nombre de la compañía</label>
              <input value={newCo.name} onChange={e => setNewCo(n => ({ ...n, name: e.target.value }))} required placeholder="Ej: MAPFRE" />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 100 }}>
              <label className="form-label">% Comisión</label>
              <input type="number" min="0" max="100" step="0.5" value={newCo.commissionRate} onChange={e => setNewCo(n => ({ ...n, commissionRate: Number(e.target.value) }))} />
            </div>
            <button type="submit" className="btn btn-amber">Agregar</button>
          </form>
        </div>
      )}

      {/* Summary */}
      <div className="stats-grid" style={{ marginBottom: 28 }}>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--amber)', background: 'rgba(245,166,35,0.05)' }}>
          <div className="stat-label" style={{ color: 'var(--amber)' }}>💰 Comisión total estimada</div>
          <div className="stat-value" style={{ color: 'var(--amber)' }}>${totalEstimated.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
          <div className="stat-sub">sobre {active.length} pólizas activas</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--blue)' }}>
          <div className="stat-label">Total primas activas</div>
          <div className="stat-value">${totalPremium.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
          <div className="stat-sub">suma de todas las primas</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--accent)' }}>
          <div className="stat-label">Compañías activas</div>
          <div className="stat-value">{companyData.filter(c => c.count > 0).length}</div>
          <div className="stat-sub">con pólizas vigentes</div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 20 }}>COMISIÓN ESTIMADA POR COMPAÑÍA ($)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ left: -10 }}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11, fontFamily: 'var(--font-display)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip formatter={v => ['$' + v.toLocaleString('es-UY'), 'Comisión']} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(245,166,35,0.06)' }} />
              <Bar dataKey="comision" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill="var(--amber)" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Compañía</th><th>Pólizas activas</th><th>Total primas</th><th>% Comisión</th><th>Comisión estimada</th><th></th></tr></thead>
            <tbody>
              {companyData.map(co => (
                <React.Fragment key={co.id}>
                <tr>
                  <td style={{ fontWeight: 600, fontFamily: 'var(--font-display)' }}>{co.name}</td>
                  <td>{co.count}</td>
                  <td style={{ fontFamily: 'var(--font-display)' }}>${co.totalPremium.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</td>
                  <td>
                    {editing === co.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="number" value={editVal} onChange={e => setEditVal(e.target.value)} style={{ width: 70, padding: '4px 8px', fontSize: 13 }} min="0" max="100" step="0.5" />
                        <span style={{ fontSize: 13 }}>%</span>
                        <button className="btn btn-primary btn-sm" style={{ padding: '4px 8px' }} onClick={() => saveRate(co.id)}><Save size={12} /></button>
                        <button className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }} onClick={() => setEditing(null)}><X size={12} /></button>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--amber)', fontWeight: 600, fontFamily: 'var(--font-display)' }}>
                        {co.commissionRate}%
                        {co.ramoRates && Object.keys(co.ramoRates).length > 0 && (
                          <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 6 }}>+{Object.keys(co.ramoRates).length} ramo{Object.keys(co.ramoRates).length > 1 ? 's' : ''}</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td style={{ color: 'var(--amber)', fontWeight: 700, fontFamily: 'var(--font-display)', fontSize: 15 }}>
                    ${co.estimated.toLocaleString('es-UY', { maximumFractionDigits: 0 })}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(co.id); setEditVal(String(co.commissionRate)); }}><Edit2 size={12} /></button>
                    <button className="btn btn-secondary btn-sm" style={{ marginLeft: 4 }} onClick={() => { setExpandRamos(expandRamos === co.id ? null : co.id); setNuevoRamo({ ramo: '', rate: '' }); }}>
                      Ramos
                    </button>
                  </td>
                </tr>
                {expandRamos === co.id && (
                  <tr>
                    <td colSpan={6} style={{ background: 'var(--bg-input)', padding: '14px 16px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
                        Comisiones especiales por ramo para {co.name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(general: {co.commissionRate}%)</span>
                      </div>
                      {co.ramoRates && Object.keys(co.ramoRates).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                          {Object.entries(co.ramoRates).map(([ramo, rate]) => (
                            <span key={ramo} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--amber-dim)', color: 'var(--amber)', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
                              {RAMO_LABELS[ramo] || ramo}: {rate}%
                              <button onClick={() => quitarRamo(co, ramo)} style={{ background: 'none', border: 'none', color: 'var(--amber)', cursor: 'pointer', padding: 0, display: 'flex' }}><X size={12} /></button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Ramo</label>
                          <select value={nuevoRamo.ramo} onChange={e => setNuevoRamo(n => ({ ...n, ramo: e.target.value }))} style={{ fontSize: 13 }}>
                            <option value="">Seleccionar...</option>
                            {RAMOS.filter(r => !(co.ramoRates && co.ramoRates[r] != null)).map(r => <option key={r} value={r}>{RAMO_LABELS[r]}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>% Comisión</label>
                          <input type="number" min="0" max="100" step="0.5" value={nuevoRamo.rate} onChange={e => setNuevoRamo(n => ({ ...n, rate: e.target.value }))} style={{ width: 90, fontSize: 13 }} placeholder="Ej: 20" />
                        </div>
                        <button className="btn btn-amber btn-sm" onClick={() => agregarRamo(co)} disabled={!nuevoRamo.ramo || nuevoRamo.rate === ''}>
                          <Plus size={13} /> Agregar excepción
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Liquidaciones recibidas por mail (las carga el bot de correo) */}
      {liquidaciones.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Liquidaciones recibidas por mail</h3>
          <p className="page-subtitle" style={{ marginBottom: 14 }}>Las carga el bot de correo desde los mails de las compañías. Privado.</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Compañía</th><th>Período</th><th style={{ textAlign: 'right' }}>Importe</th><th>Mail</th><th>Adjuntos</th>
                </tr>
              </thead>
              <tbody>
                {liquidaciones.map(l => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 600 }}>{l.company || '—'}</td>
                    <td>{l.periodo || '—'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {l.amount ? `${l.currency === 'USD' ? 'U$S' : '$'} ${Number(l.amount).toLocaleString('es-UY', { minimumFractionDigits: 2 })}` : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {l.asunto}
                      {l.fecha && <div>{new Date(l.fecha).toLocaleDateString('es-UY')}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {(l.adjuntos || []).map((a, i) => (
                        <a key={i} href={`${a.ruta}?t=${encodeURIComponent(localStorage.getItem('nebb_token') || '')}`}
                           target="_blank" rel="noopener noreferrer" style={{ display: 'block', color: 'var(--accent)' }}>
                          {a.name}
                        </a>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
